import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import styled, { keyframes } from "styled-components";
import SiteHeader from "../landing/components/Header/SiteHeader";
import HeroSection from "../landing/components/Hero/HeroSection";
import HeroCarousel from "../landing/components/Hero/HeroCarousel";
import LeadershipSection from "../landing/components/Leadership/LeadershipSection";
import SchemesBanner from "../landing/components/SchemesBanner/SchemesBanner";
import DashboardSection from "../landing/components/Dashboard/DashboardSection";
import Footer from "../landing/components/Footer/Footer";
import DashboardCard from "../landing/components/Dashboard/DashboardCard";
import VideoSection from "../landing/components/VideoSection/VideoSection";
import spotlight from "../../assets/images/spotlight.png";
import Ticker_star from "../landing/components/Hero/Ticker_star.png";
import { updates } from "./data/dashboardData";

import PlaybackButton from "./components/PlaybackButton";
import useMovingContentAccessibility from "../../hooks/useMovingContentAccessibility";

const LandingPage = () => {
  const tickerViewportRef = useMovingContentAccessibility();
  const [isTickerPaused, setIsTickerPaused] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    const isPageReload = navigationEntry?.type === "reload";
    const previousScrollRestoration = window.history.scrollRestoration;

    window.history.scrollRestoration = "manual";

    if (isPageReload) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    }

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    const autoTranslateTimer = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("bhashini-language-select", {
          detail: { languageCode: "hi" },
        }),
      );
    }, 1200);

    return () => window.clearTimeout(autoTranslateTimer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nextIsScrolled = window.scrollY > 8;
      setIsHeaderScrolled((currentIsScrolled) =>
        currentIsScrolled === nextIsScrolled
          ? currentIsScrolled
          : nextIsScrolled,
      );
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <LandingRoot>
      <SiteHeader isScrolled={isHeaderScrolled} />

      <main id="main-content" tabIndex={-1}>
      <ThemeWrapper>
        <h1 className="sr-only">National Minorities Development &amp; Finance Corporation (NMDFC)</h1>
        <HeroSection />
        <HeroCarousel />
      </ThemeWrapper>
      <NewsTicker aria-label="Latest updates">
        <TickerViewport ref={tickerViewportRef} onFocusCapture={() => setIsTickerPaused(true)}>
          <TickerTrack style={{ animationPlayState: isTickerPaused ? "paused" : undefined }}>
            {[0, 1].map((copy) => (
              <TickerGroup key={copy} data-print-duplicate={copy === 1}>
                {updates.map((item) => (
                  <TickerItem
                    key={`${copy}-${item.title}`}
                    type="button"
                    data-moving-item
                    onClick={() => navigate(item.route || "/updates")}
                  >
                    <img src={Ticker_star} alt="" />
                    <span>{item.title}</span>
                  </TickerItem>
                ))}
              </TickerGroup>
            ))}
          </TickerTrack>
        </TickerViewport>
        <PlaybackButton paused={isTickerPaused} onClick={() => setIsTickerPaused((prev) => !prev)}
          label="latest updates scrolling" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "#ffffff", color: "#173f86", borderColor: "#ffffff" }} />
      </NewsTicker>
      <LeaderShipWrapper data-print-flow>
        <div className="left">
          <SchemesBanner />
          <DashboardCard />
        </div>
        <div className="right">
          <LeadershipSection />
        </div>
      </LeaderShipWrapper>

      <VideoSection />
      <DashboardSection />
      </main>

      <Footer />
    </LandingRoot>
  );
};

const LandingRoot = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  overflow-x: clip;
  background-color: #f6f6f6;

  > * {
    max-width: 100%;
    min-width: 0;
  }

  section,
  article,
  main,
  aside {
    max-width: 100%;
    min-width: 0;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  a,
  button,
  span {
    overflow-wrap: anywhere;
  }
`;

const ThemeWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background-image: url(${spotlight});
  background-position: center top;
  background-size: cover;
  overflow: hidden;
`;

const tickerRun = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
`;

const NewsTicker = styled.div`
  position: relative;
  padding-right: 54px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 66px;
  border-top: 6px solid #fac03b;
  background-color: #005f5b;
  background-image: linear-gradient(90deg, #005f5b 0%, #174586 100%);
  color: #ffffff;
  overflow: hidden;
`;

const TickerViewport = styled.div`
  min-height: 60px;
  display: flex;
  align-items: center;
  overflow: hidden;

  &:hover > div {
    animation-play-state: paused;
  }
`;

const TickerTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${tickerRun} 42s linear infinite;
`;

const TickerGroup = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 52px;
  padding-right: 52px;

  @media (max-width: 768px) {
    gap: 28px;
    padding-right: 28px;
  }
`;

const TickerItem = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  white-space: nowrap;
  font-size: 20px;
  line-height: 1;

  &:focus-visible {
    outline: 2px solid #fac03b;
    outline-offset: 6px;
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    gap: 10px;
    font-size: 15px;

    img {
      width: 18px;
      height: 18px;
    }
  }
`;

const LeaderShipWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
  gap: clamp(18px, 2vw, 28px);
  padding: clamp(20px, 3vw, 40px);
  align-items: stretch;
  max-width: 1480px;
  width: 100%;
  margin: 40px auto 0;

  .left {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-width: 0;
    min-height: 0;
  }

  .left > * {
    min-height: clamp(500px, 43vw, 590px);
  }

  .right {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  .right > * {
    flex: 1 1 auto;
    min-height: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    margin-top: 20px;

    .left > * {
      min-height: 520px;
    }
  }

  @media (max-width: 640px) {
    padding: 16px;
    gap: 16px;

    .left {
      gap: 16px;
    }

    .left > * {
      min-height: auto;
    }
  }
`;

export default LandingPage;
